import type { QuizQuestion } from '@/lib/services/questions';

export interface QuizWebPreviewModel {
  quizId: string;
  editionId: string;
  slug: string;
  title: string;
  partnerId: string | null;
  socialLeaderboardEnabled: boolean;
  questions: QuizQuestion[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isPollOption(value: unknown): boolean {
  return (
    isRecord(value) &&
    nonEmptyString(value.id) &&
    nonEmptyString(value.label) &&
    Number.isSafeInteger(value.sort_index) &&
    Number(value.sort_index) >= 0
  );
}

function isQuizQuestion(value: unknown): value is QuizQuestion {
  if (
    !isRecord(value) ||
    !Number.isSafeInteger(value.question_id) ||
    !Number.isSafeInteger(value.question_index) ||
    Number(value.question_index) < 0
  ) {
    return false;
  }

  if (value.item_type === 'poll') {
    return (
      nonEmptyString(value.poll_definition_id) &&
      nonEmptyString(value.poll_prompt) &&
      Array.isArray(value.poll_options) &&
      value.poll_options.length > 0 &&
      value.poll_options.every(isPollOption)
    );
  }

  return (
    nonEmptyString(value.stem) &&
    nonEmptyString(value.key) &&
    nonEmptyString(value.distractor_1) &&
    nonEmptyString(value.distractor_2) &&
    nonEmptyString(value.distractor_3)
  );
}

export function parseQuizWebPreviewModel(value: unknown): QuizWebPreviewModel | null {
  if (
    !isRecord(value) ||
    !nonEmptyString(value.quizId) ||
    !nonEmptyString(value.editionId) ||
    !nonEmptyString(value.slug) ||
    !nonEmptyString(value.title) ||
    (value.partnerId !== null && !nonEmptyString(value.partnerId)) ||
    typeof value.socialLeaderboardEnabled !== 'boolean' ||
    !Array.isArray(value.questions) ||
    value.questions.length === 0 ||
    !value.questions.every(isQuizQuestion)
  ) {
    return null;
  }

  const positions = new Set(value.questions.map((question) => question.question_index));
  if (positions.size !== value.questions.length) return null;

  return {
    quizId: value.quizId.trim(),
    editionId: value.editionId.trim(),
    slug: value.slug.trim(),
    title: value.title.trim(),
    partnerId: value.partnerId?.trim() ?? null,
    socialLeaderboardEnabled: value.socialLeaderboardEnabled,
    questions: [...value.questions].sort(
      (left, right) => left.question_index - right.question_index
    ),
  };
}
